// Unified undo/redo stack for all editor actions
// Singleton — one stack across all state managers

import { debug } from '@/utils/debug'

/** A reversible command */
export interface UndoCommand {
  readonly description: string
  undo(): void
  redo(): void
}

type ChangeCallback = () => void

const MAX_STACK_SIZE = 50

class UndoStackManager {
  private undoStack: UndoCommand[] = []
  private redoStack: UndoCommand[] = []
  private changeCallbacks: ChangeCallback[] = []

  push(command: UndoCommand): void {
    this.undoStack.push(command)
    this.redoStack.length = 0 // Clear redo on new action

    // Cap at max size
    if (this.undoStack.length > MAX_STACK_SIZE) {
      this.undoStack.shift()
    }

    debug.log('Undo', `Push: ${command.description} (stack: ${this.undoStack.length})`)
    this.notifyChange()
  }

  undo(): void {
    const command = this.undoStack.pop()
    if (!command) return

    command.undo()
    this.redoStack.push(command)
    debug.log('Undo', `Undo: ${command.description}`)
    this.notifyChange()
  }

  redo(): void {
    const command = this.redoStack.pop()
    if (!command) return

    command.redo()
    this.undoStack.push(command)
    debug.log('Undo', `Redo: ${command.description}`)
    this.notifyChange()
  }

  canUndo(): boolean {
    return this.undoStack.length > 0
  }

  canRedo(): boolean {
    return this.redoStack.length > 0
  }

  clear(): void {
    this.undoStack.length = 0
    this.redoStack.length = 0
    this.notifyChange()
  }

  /** Subscribe to stack changes (for UI button state) */
  onChange(callback: ChangeCallback): () => void {
    this.changeCallbacks.push(callback)
    return () => {
      const index = this.changeCallbacks.indexOf(callback)
      if (index >= 0) this.changeCallbacks.splice(index, 1)
    }
  }

  private notifyChange(): void {
    for (const cb of this.changeCallbacks) {
      try { cb() } catch (e) { debug.error('Undo', 'Error in change callback', e) }
    }
  }
}

export const undoStack = new UndoStackManager()
